// src/pages/Home.js
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Scan, Shield, Cloud, Zap } from 'lucide-react';

const features = [
  {
    icon: Scan,
    title: 'Smart Scanning',
    description: 'Advanced document scanning with automatic edge detection and enhancement.'
  },
  {
    icon: Shield,
    title: 'Secure Storage',
    description: 'End-to-end encryption ensures your documents stay private and secure.'
  },
  {
    icon: Cloud,
    title: 'Cloud Sync',
    description: 'Access your documents from anywhere with seamless cloud synchronization.'
  },
  {
    icon: Zap,
    title: 'Fast Processing',
    description: 'Lightning-fast document processing and organization capabilities.'
  }
];

export default function Home() {
  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
            >
              Document Management
              <br />
              Made Simple
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Scan, organize, and access your documents from anywhere. 
              Powerful features wrapped in a beautiful, easy-to-use interface.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a href="https://app.docu.com" className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-white bg-blue-600 hover:bg-blue-700">
                Get Started Free <ArrowRight className="ml-2" size={20} />
              </a>
              <a href="#demo" className="inline-flex items-center px-8 py-3 border-2 border-blue-600 text-base font-medium rounded-full text-blue-600 hover:bg-blue-50">
                Watch Demo
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white" id="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">
              Everything you need in one place
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Powerful features to help you manage documents efficiently
            </p>
          </div>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="absolute -top-4 left-8">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <h3 className="mt-8 text-xl font-semibold text-gray-900">{feature.title}</h3>
                <p className="mt-4 text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white">
            Ready to get started?
          </h2>
          <p className="mt-4 text-xl text-white/80">
            Join thousands of users who trust Docu for their document management needs.
          </p>
          <div className="mt-10">
            <a href="https://app.docu.com" className="inline-flex items-center px-8 py-3 border-2 border-white text-base font-medium rounded-full text-white hover:bg-white hover:text-blue-600 transition-colors">
              Try Docu Free <ArrowRight className="ml-2" size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}